'use client';
import React, { useEffect } from 'react';
import { ArrowRight, ArrowLeft, Info, TrendingUp, Clock } from 'lucide-react';
import type { DeliveryFormData } from './BookDeliveryWizard';

// BACKEND INTEGRATION: GET /api/pricing/rates — fetch from admin-configured pricing engine
const PRICING_ENGINE = {
  baseFare: 3500,
  distanceTiers: [
    { min: 0, max: 5, pricePerKm: 800 },
    { min: 5, max: 10, pricePerKm: 650 },
    { min: 10, max: 20, pricePerKm: 550 },
    { min: 20, max: Infinity, pricePerKm: 450 },
  ],
  fragileCharge: 1000,
  peakHourSurcharge: 500,
  minCharge: 4000,
};

function calculatePrice(distanceKm: number, fragile: boolean): {
  baseFare: number;
  distanceCharge: number;
  fragileCharge: number;
  peakSurcharge: number;
  total: number;
  isPeak: boolean;
} {
  const tier = PRICING_ENGINE.distanceTiers.find(t => distanceKm >= t.min && distanceKm < t.max);
  const distanceCharge = Math.round((tier?.pricePerKm ?? 450) * distanceKm);
  const fragileCharge = fragile ? PRICING_ENGINE.fragileCharge : 0;
  const hour = new Date().getHours();
  const isPeak = (hour >= 7 && hour <= 9) || (hour >= 17 && hour <= 20);
  const peakSurcharge = isPeak ? PRICING_ENGINE.peakHourSurcharge : 0;
  const subtotal = PRICING_ENGINE.baseFare + distanceCharge + fragileCharge + peakSurcharge;
  const total = Math.max(subtotal, PRICING_ENGINE.minCharge);
  return {
    baseFare: PRICING_ENGINE.baseFare,
    distanceCharge,
    fragileCharge,
    peakSurcharge,
    total,
    isPeak,
  };
}

interface StepPricingProps {
  formData: DeliveryFormData;
  updateForm: (partial: Partial<DeliveryFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function StepPricing({ formData, updateForm, onNext, onBack }: StepPricingProps) {
  const pricing = calculatePrice(formData.distanceKm, formData.fragile);

  useEffect(() => {
    updateForm({
      estimatedPrice: pricing.total,
      basefare: pricing.baseFare,
      distanceCharge: pricing.distanceCharge,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const estimatedMinutes = Math.round(formData.distanceKm * 4 + 10);

  const tierLabel = formData.distanceKm <= 5
    ? '0–5 km (Standard)'
    : formData.distanceKm <= 10
    ? '5–10 km (Medium)'
    : formData.distanceKm <= 20
    ? '10–20 km (Long)'
    : 'Above 20 km (Extended)';

  return (
    <div className="space-y-5">
      {/* Route Summary */}
      <div className="card-elevated p-5">
        <h3 className="text-base font-semibold text-foreground mb-4">Muhtasari wa Safari</h3>
        <div className="flex items-center gap-4">
          <div className="flex-1 p-3 bg-green-50 rounded-xl border border-green-200">
            <p className="text-[10px] font-semibold text-green-700 uppercase tracking-wide mb-1">Kutoka</p>
            <p className="text-sm font-medium text-foreground truncate">{formData.pickupAddress}</p>
            {formData.pickupLandmark && <p className="text-xs text-muted-foreground truncate">{formData.pickupLandmark}</p>}
          </div>
          <div className="text-center">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
              <ArrowRight size={18} className="text-primary" />
            </div>
            <p className="text-xs font-bold text-foreground mt-1 font-tabular">{formData.distanceKm.toFixed(1)} km</p>
          </div>
          <div className="flex-1 p-3 bg-red-50 rounded-xl border border-red-200">
            <p className="text-[10px] font-semibold text-red-700 uppercase tracking-wide mb-1">Kwenda</p>
            <p className="text-sm font-medium text-foreground truncate">{formData.deliveryAddress}</p>
            {formData.deliveryLandmark && <p className="text-xs text-muted-foreground truncate">{formData.deliveryLandmark}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3 p-2 bg-muted/50 rounded-lg">
          <Clock size={14} className="text-muted-foreground" />
          <p className="text-xs text-muted-foreground">Muda wa kukadiriwa: <span className="font-semibold text-foreground">{estimatedMinutes}–{estimatedMinutes + 15} dakika</span></p>
        </div>
      </div>

      {/* Price Breakdown */}
      <div className="card-elevated p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-foreground">Hesabu ya Bei</h3>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">{tierLabel}</span>
        </div>

        <div className="space-y-3">
          <PriceLine label="Nauli ya Msingi (Base Fare)" amount={pricing.baseFare} />
          <PriceLine label={`Malipo ya Umbali (${formData.distanceKm.toFixed(1)} km)`} amount={pricing.distanceCharge} />
          {pricing.fragileCharge > 0 && (
            <PriceLine label="Malipo ya Fragile" amount={pricing.fragileCharge} highlight />
          )}
          {pricing.isPeak && pricing.peakSurcharge > 0 && (
            <PriceLine label="Peak Hour Surcharge" amount={pricing.peakSurcharge} highlight />
          )}

          {pricing.isPeak && (
            <div className="flex items-start gap-2 p-2.5 bg-amber-50 rounded-lg border border-amber-200">
              <TrendingUp size={14} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700 font-medium">Saa za msongamano — bei ya ziada imetumika (Peak hours surcharge applied)</p>
            </div>
          )}

          <div className="border-t-2 border-dashed border-border pt-3 mt-1">
            <div className="flex items-center justify-between">
              <span className="text-base font-bold text-foreground">Jumla ya Kulipa</span>
              <span className="text-2xl font-extrabold text-primary tsh-value">TSh {pricing.total.toLocaleString()}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Bei ya kukadiriwa — inaweza kubadilika kidogo kulingana na umbali halisi</p>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="flex items-start gap-2.5 p-3.5 bg-blue-50 rounded-xl border border-blue-200">
        <Info size={15} className="text-blue-600 mt-0.5 shrink-0" />
        <p className="text-xs text-blue-700">Bei hii inajumuisha kinga ya msingi. Kwa vitu vya thamani kubwa, tunapendekeza kununua bima ya ziada.</p>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 btn-secondary py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
        >
          <ArrowLeft size={16} />
          Rudi Nyuma
        </button>
        <button
          type="button"
          onClick={onNext}
          className="flex-1 btn-primary py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
        >
          Chagua Malipo
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

function PriceLine({ label, amount, highlight = false }: { label: string; amount: number; highlight?: boolean }) {
  return (
    <div className={`flex items-center justify-between py-1 ${highlight ? 'text-amber-700' : ''}`}>
      <span className={`text-sm ${highlight ? 'font-medium' : 'text-muted-foreground'}`}>{label}</span>
      <span className={`text-sm font-semibold tsh-value ${highlight ? 'text-amber-700' : 'text-foreground'}`}>
        TSh {amount.toLocaleString()}
      </span>
    </div>
  );
}